import styles from './WorkHours.module.scss';

import WarningIcon from '@mui/icons-material/Warning';
import { Alert, Box } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { DateTime, Duration } from 'luxon';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DateTimeInputDialog } from '@work4all/components/lib/dialogs/DateTimeInputDialog';

import { useUser } from '@work4all/data';

import {
  checkUserRight,
  UserRights,
} from '@work4all/models/lib/additionalEnums/UserRights';

import { DateTimeCustom } from '@work4all/utils/lib/date-utils/formatDateString';

import { MAX_HOURS_PRESENT } from '../calendar/constants';
import { useCalendarHoursConfig } from '../calendar/hooks/use-calendar-hours-config';

import { Card, CardHeader } from './components';
import { useWorkTimeCheckin } from './hooks/useWorkTimeCheckin';
import {
  TIMETRACKER_LAYOUTS,
  TimeTrackerControls,
} from './TimeTrackerControls';
import { TimeTrackerInputSettings } from './TimeTrackerInputSettings';
import { TimeTracker } from './use-time-tracker';
import { checkIsBeforeEarliestCheckinTime } from './utils/checkIsBeforeEarliestCheckingTime';
import { getDefaultTime } from './utils/getDefaultTime';
import { WorkTimeDisabledAlert } from './WorkTimeDisableAlert';

export interface WorkHoursProps {
  tracker: TimeTracker;
  renderHeader?: boolean;
}

export function WorkHours(props: WorkHoursProps) {
  const { tracker, renderHeader = true } = props;

  const { t } = useTranslation();
  const user = useUser();
  const workTime = useCalendarHoursConfig();

  const [showWorkTimeAlert, setShowWorkTimeAlert] = useState(false);

  const {
    dialog,
    earliestCheckinTime,
    closeDialog,
    determineCheckinCheckoutTime,
  } = useWorkTimeCheckin(tracker);

  const isBeforeEarliestCheckinTime =
    checkIsBeforeEarliestCheckinTime(earliestCheckinTime);

  const { result } = tracker;

  const earliestStart = useMemo(() => {
    if (
      result === null ||
      result.state !== 'initial' ||
      !user?.kommtGehtMinimaleStartZeit
    ) {
      return null;
    }

    const timeNow = DateTime.now();

    if (timeNow > earliestCheckinTime) {
      return null;
    }

    return t('TIME_TRACKER.EARLIEST_START', {
      time: earliestCheckinTime.toLocaleString(DateTime.TIME_SIMPLE),
    });
  }, [result, user?.kommtGehtMinimaleStartZeit, earliestCheckinTime, t]);

  if (result == null) {
    return null;
  }

  const isRunningForMoreThanMaxHours =
    result.totalPresent.as('hours') > MAX_HOURS_PRESENT;

  const emptyPeriod = `${Duration.fromMillis(0).toFormat('hh:mm')} h`;
  const isPresentBeforeEarliestCheckinTime =
    result.isPresent && isBeforeEarliestCheckinTime;
  const presenceTime = isPresentBeforeEarliestCheckinTime
    ? emptyPeriod
    : `${result.totalPresent.toFormat('hh:mm')} h`;
  const totalWorkingTime = isPresentBeforeEarliestCheckinTime
    ? emptyPeriod
    : `${result.totalWorked.toFormat('hh:mm')} h`;

  return (
    <>
      <DateTimeInputDialog
        open={dialog.open}
        title={t(`TIME_TRACKER.DIALOG.${dialog.type}.title`)}
        description={t(`TIME_TRACKER.DIALOG.${dialog.type}.description`)}
        presentSince={dialog.type === 'stop' && result.presentSince}
        onConfirm={(dateTime) => {
          tracker[dialog.type](dateTime);
          closeDialog();
        }}
        onCancel={closeDialog}
        defaultValue={getDefaultTime({ ...tracker }, workTime.workDayEnd)}
        {...TimeTrackerInputSettings(tracker, dialog.type)}
      />

      <WorkTimeDisabledAlert
        open={showWorkTimeAlert}
        onClose={() => setShowWorkTimeAlert(false)}
      />

      <Card>
        {renderHeader ? (
          <CardHeader
            title={
              <Box p="1rem 0">
                {t('COMMON.TODAY')},&nbsp;
                {DateTime.now().toLocaleString(DateTimeCustom.DATE_SHORT)}
              </Box>
            }
          >
            {isRunningForMoreThanMaxHours ? (
              <div className={styles.maxHoursError}>
                <WarningIcon fontSize="medium" color="error" />
                <Typography variant="caption" color="error">
                  {t('TIME_TRACKER.MAX_HOURS_WARNING', {
                    hours: MAX_HOURS_PRESENT,
                  })}
                </Typography>
              </div>
            ) : null}
          </CardHeader>
        ) : null}

        <TimeTrackerControls
          layout={TIMETRACKER_LAYOUTS.RIBBON}
          disabled={tracker.disabled}
          state={result.state}
          hidePause={isBeforeEarliestCheckinTime}
          presentSince={result.presentSince}
          pausedSince={result.pausedSince}
          stoppedSince={result.stoppedSince}
          onStart={() => {
            if (tracker.disabled) {
              setShowWorkTimeAlert(true);
            } else {
              determineCheckinCheckoutTime('start')();
            }
          }}
          onStop={determineCheckinCheckoutTime('stop')}
          onPause={
            checkUserRight(UserRights.PausenStempeln, user.benutzerRechte)
              ? () => {
                  tracker.pause(new Date());
                }
              : undefined
          }
          onResume={() => tracker.resume(new Date())}
        />

        <div className={styles.stats}>
          <Tooltip title={t('TIME_TRACKER.PRESENCE_TOOLTIP')}>
            <div tabIndex={0} className={styles.stat}>
              <Typography variant="caption" color="text.tertiary">
                {t('TIME_TRACKER.PRESENCE')}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {presenceTime}
              </Typography>
            </div>
          </Tooltip>

          <Tooltip title={t('TIME_TRACKER.BREAK_TIME_TOOLTIP')}>
            <div tabIndex={0} className={styles.stat}>
              <Typography variant="caption" color="text.tertiary">
                {t('TIME_TRACKER.BREAK_TIME')}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {result.totalPaused.toFormat('hh:mm')} h
              </Typography>
            </div>
          </Tooltip>

          <Tooltip title={t('TIME_TRACKER.WORKING_TIME_TOOLTIP')}>
            <div tabIndex={0} className={styles.stat}>
              <Typography variant="caption" color="text.tertiary">
                {t('TIME_TRACKER.WORKING_TIME')}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {totalWorkingTime}
              </Typography>
            </div>
          </Tooltip>
        </div>

        {earliestStart && (
          <Alert
            sx={{
              backgroundColor: '#D9A931',
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
              color: '#ffffff',
            }}
            icon={false}
          >
            <Typography variant="h5">{earliestStart}</Typography>
          </Alert>
        )}
      </Card>
    </>
  );
}
